export function applyScorecardResponse(result, setters, options = {}) {
  if (!result?.modes || !setters) return;

  const {
    setDataBar,
    setRegimeBar,
    setRegimeBarForNocrash,
    setYearsList,
    setBins,
    setRegimeSelectors,
    setMinMaxAvgArray,
    setMinMaxAvgArrayNoCrash,
    setMinMaxAvgAssetArray,
    setMinMaxAvgAssetArrayNocrash,
    setMinMaxAvgAssetReshuffleArray,
    setMixAssetArray,
    setMixAssetReshuffleArray,
    setMinMaxAvgMixNoCrash,
    setAmValues,
    setGmValues,
    setMixAmGm,
    setMixAmGmReshuffle,
    setAmGmMixNoCrash,
    setPercentile2DataMaxAllocation,
    setChart5ByMode,
    setLoading,
  } = setters;

  const regime = result.regime || {};
  if (setDataBar && regime.dataBar) setDataBar(regime.dataBar);
  if (setRegimeBar && regime.regimeBar) setRegimeBar(regime.regimeBar);
  if (setRegimeBarForNocrash && regime.regimeBarForNocrash) {
    setRegimeBarForNocrash(regime.regimeBarForNocrash);
  }
  if (setYearsList && regime.yearsList) setYearsList(regime.yearsList);
  if (setBins && regime.bins) setBins(regime.bins);
  // Do not write regimeSelectors back — avoids refetch loops when the API echoes cuts.

  const normal = result.modes.normal;
  const reshuffle = result.modes.reshuffle;
  const nocrash = result.modes.nocrash;
  const activeModeKey =
    options.mode && result.modes[options.mode]
      ? options.mode
      : normal
        ? "normal"
        : reshuffle
          ? "reshuffle"
          : nocrash
            ? "nocrash"
            : null;
  const activeMode = activeModeKey ? result.modes[activeModeKey] : null;

  if (normal) {
    setMinMaxAvgArray?.({
      minArray: normal.base?.min || [],
      maxArray: normal.base?.max || [],
      avgArray: normal.base?.avg || [],
    });
    setMinMaxAvgAssetArray?.({
      minAssetArray: normal.other?.min || [],
      maxAssetArray: normal.other?.max || [],
      averageAssetArray: normal.other?.avg || [],
    });
    setMixAssetArray?.({
      mixMinAssetArray: normal.mix?.min || [],
      mixMaxAssetArray: normal.mix?.max || [],
      mixAvgAssetArray: normal.mix?.avg || [],
    });
    setAmValues?.((p) => ({
      ...p,
      am: normal.base?.am,
      amMean: normal.other?.am,
    }));
    setGmValues?.((p) => ({
      ...p,
      gm: normal.base?.gm,
    }));
    setMixAmGm?.({
      mixAM: normal.mix?.am,
      mixGM: normal.mix?.gm,
    });
  }

  if (reshuffle) {
    setMinMaxAvgAssetReshuffleArray?.({
      minAssetReshuffleArray: reshuffle.other?.min || [],
      maxAssetReshuffleArray: reshuffle.other?.max || [],
      averageAssetReshuffleArray: reshuffle.other?.avg || [],
    });
    setMixAssetReshuffleArray?.({
      mixMinAssetReshuffleArray: reshuffle.mix?.min || [],
      mixMaxAssetReshuffleArray: reshuffle.mix?.max || [],
      mixAvgAssetReshuffleArray: reshuffle.mix?.avg || [],
    });
    setAmValues?.((p) => ({
      ...p,
      amMeanReshuffle: reshuffle.other?.am,
    }));
    setMixAmGmReshuffle?.({
      mixAMReshuffle: reshuffle.mix?.am,
      mixGMReshuffle: reshuffle.mix?.gm,
    });
  }

  if (nocrash) {
    setMinMaxAvgArrayNoCrash?.({
      minArrayNocrash: nocrash.base?.min || [],
      maxArrayNocrash: nocrash.base?.max || [],
      averageArrayNocrash: nocrash.base?.avg || [],
    });
    setMinMaxAvgAssetArrayNocrash?.({
      minAssetArrayNocrash: nocrash.other?.min || [],
      maxAssetArrayNocrash: nocrash.other?.max || [],
      averageAssetArrayNocrash: nocrash.other?.avg || [],
    });
    setMinMaxAvgMixNoCrash?.({
      minMixNoCrash: nocrash.mix?.min || [],
      maxMixNoCrash: nocrash.mix?.max || [],
      avgMixNoCrash: nocrash.mix?.avg || [],
    });
    setAmValues?.((p) => ({
      ...p,
      amNocrash: nocrash.base?.am,
      amMeanNocrash: nocrash.other?.am,
    }));
    setGmValues?.((p) => ({
      ...p,
      gmNocrash: nocrash.base?.gm,
    }));
    setAmGmMixNoCrash?.({
      amMixNoCrash: nocrash.mix?.am,
      gmMixNoCrash: nocrash.mix?.gm,
    });
  }

  if (
    setPercentile2DataMaxAllocation &&
    activeMode?.chart5?.recommendedAlloc != null
  ) {
    setPercentile2DataMaxAllocation(activeMode.chart5.recommendedAlloc);
  }

  if (setChart5ByMode) {
    setChart5ByMode((prev) => ({
      ...prev,
      ...(normal?.chart5 ? { normal: normal.chart5 } : {}),
      ...(reshuffle?.chart5 ? { reshuffle: reshuffle.chart5 } : {}),
      ...(nocrash?.chart5 ? { nocrash: nocrash.chart5 } : {}),
    }));
  }

  setLoading?.(false);
}

export const SCORECARD_CARTOON_CONFIG = {
  "Store-of-Value [CPI+2%]": {
    type: "Store-of-Value CPI",
    binValues: [2, 2, 2, 2],
    usesCpi: true,
  },
  "Store-of-Value [7%]": {
    type: "Store-of-Value",
    binValues: [7, 7, 7, 7],
    usesCpi: false,
  },
  "Alpha [20,7.5,5,5]": {
    type: "Alpha",
    binValues: [20, 7.5, 5, 5],
    usesCpi: false,
  },
  "Alpha [15,10,2.5,2.5]": {
    type: "Alpha",
    binValues: [15, 10, 2.5, 2.5],
    usesCpi: false,
  },
  "INSUR[10-to-1]": {
    type: "Insurance",
    binValues: [1000, -100, -100, -100],
    usesCpi: false,
  },
  "INSUR[12-to-1]": {
    type: "Insurance",
    binValues: [1200, -100, -100, -100],
    usesCpi: false,
  },
  "INSUR[15-to-1]": {
    type: "Insurance",
    binValues: [1500, -100, -100, -100],
    usesCpi: false,
  },
};

export function resolveCartoonConfig(assetValue) {
  return SCORECARD_CARTOON_CONFIG[assetValue] || null;
}
